import { gql, useQuery } from "@apollo/client";
import moment from "moment";
import React, { useState } from "react";
import { DownloadExcel } from "src/components/DownloadExcel";
import { TableBlueprint } from "src/components/table";
import { Graph } from "src/generated/graph";
import { FormatSaleList } from "src/pages/reports/salesummary";
import { BreadCrumb, BreadCrumbProp } from "../../components/light/Breadcrumb";
import { Content } from "../../components/light/Content";
import { SectionBlock } from "../../components/light/SectionBlock";
import { Loading } from "../../components/Loading";

const MERCHANT_TOTAL_DETAIL = gql`
  query supplierOrderBalance($offset: Int = 0, $limit: Int = 100) {
    supplierOrderBalance(offset: $offset, limit: $limit, status: TOTAL) {
      data
      page {
        current
        size
        total
      }
    }
  }
`;

const BreadCrumbItems: BreadCrumbProp[] = [
  {
    text: "Dashboard",
    to: "/",
  },
  {
    text: "Total Balance",
    to: "/dashboard/balance",
  },
];

export function BalanceDetailScreen(props: any) {
  const items = [];
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const { data, loading } = useQuery<Graph.Query>(MERCHANT_TOTAL_DETAIL, {
    fetchPolicy: "cache-and-network",
    variables: {
      offset: offset * Number(limit),
      limit: Number(limit),
    },
  });

  if (loading || data === undefined) {
    return (
      <Content breadcrumb={<BreadCrumb items={BreadCrumbItems} />}>
        <SectionBlock title="Total Balance Detail" transparent={true}>
          <Loading />
        </SectionBlock>
      </Content>
    );
  }

  for (const item of data.supplierOrderBalance?.data!) {
    items.push({
      item: {
        image: item.thumb_mid,
        uuid: item.order_uuid,
        remote_stock: false,
        fbl: item.fbl,
        supplier_code: item.supplier_code,
        code: item.code,
        ostatus: item.ostatus,
        itemid: item.itemid,
        order_date: moment(item.order_date).format("YYYY-MM-DD"),
      },
      fullPrice: item.full_price ? item.full_price : 0,
      skuStock: item.sku.stockQty,
      sku: {
        color: item.sku.option1,
        size: item.sku.option2,
      },
      deliveryFee: {
        amount: item.delivery_fee ? item.delivery_fee : 0,
        ostatus: item.ostatus,
      },
      handlingFee: {
        amount: item.handling_fee ? item.handling_fee : 0,
        ostatus: item.ostatus,
      },
      commission: {
        amount: item.l192_commission ? item.l192_commission : 0,
        ostatus: item.ostatus,
      },
      netSale: {
        amount:
          Number(item.full_price ? item.full_price : 0)! -
          (Number(item.delivery_fee ? item.delivery_fee : 0) +
            Number(item.handling_fee ? item.handling_fee : 0) +
            Number(item.l192_commission ? item.l192_commission : 0)),
        ostatus: item.ostatus,
      },
      discount: (item.discounted | 0) - (item.promo_discount | 0),
    });
  }

  const format = FormatSaleList.filter((x) => x.fieldMatch !== "log").map((x) => {
    return {
      ...x,
      className: "text",
    };
  });

  return (
    <Content breadcrumb={<BreadCrumb items={BreadCrumbItems} />}>
      <SectionBlock title="Total Balance Detail" transparent={true}>
        <DownloadExcel
          query={MERCHANT_TOTAL_DETAIL}
          option={{
            fetchPolicy: "cache-and-network",
            variables: {
              offset: 0,
              limit: 1000000,
            },
          }}
          queryName="supplierOrderBalance"
        />
        <br />
        <br />
        <TableBlueprint
          {...props}
          tableTemplate="table table-padded"
          limitDisplay={10}
          limit={limit}
          setLimit={setLimit}
          offset={offset}
          setOffset={setOffset}
          totalPage={data.supplierTotalBalance?.page?.total}
          renderList={{
            items,
            format,
          }}
          customField={{
            item: (val: any) => {
              return (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={val.image}
                    alt=""
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: 4,
                      objectFit: "cover",
                      marginRight: 10,
                    }}
                  />
                  <div>
                    <div>{val.uuid}</div>
                    <div>
                      <small>{val.supplier_code}</small>
                    </div>
                    <div>
                      <small>
                        <a
                          href={"https://www.l192.com/product/" + val.itemid}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          L192: {val.code}
                        </a>
                      </small>
                    </div>
                    {val.order_date && <small>Order Date: {val.order_date}</small>}
                    {val.fbl && (
                      <div>
                        <span className="text-success">FBL</span>
                      </div>
                    )}
                    {/* { x.remote_stock && <div><span className="text-danger">Remote Stock</span></div> } */}
                  </div>
                </div>
              );
            },
            sku: (val: any) => {
              return (
                <div>
                  <small>Color: {val.color}</small>
                  <br />
                  <small>Size: {val.size}</small>
                </div>
              );
            },
            fullPrice: (val: any) => <span className="text-success">${val}</span>,
            discount: (val: any) => <span>${Number(val).toFixed(2)}</span>,
            deliveryFee: (val: any) => {
              return (
                <span className="text-danger">
                  ({val.ostatus === 3 || val.ostatus === 8 ? "$" + val.amount : "NA"})
                </span>
              );
            },
            handlingFee: (val: any) => {
              return (
                <span className="text-danger">
                  ({val.ostatus === 3 || val.ostatus === 8 ? "$" + val.amount : "NA"})
                </span>
              );
            },
            commission: (val: any) => {
              return (
                <span className="text-danger">
                  ({val.ostatus === 3 || val.ostatus === 8 ? "$" + val.amount : "NA"})
                </span>
              );
            },
            netSale: (val: any) => {
              return (
                <span className="text-success">
                  {val.ostatus === 3 || val.ostatus === 8 ? "$" + Number(val.amount).toFixed(2) : "NA"}
                </span>
              );
            },
          }}
        />
      </SectionBlock>
    </Content>
  );
}
